import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
/* global google */
/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  COLOR_SHORT_TERM_RENTAL_RANGE,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';
import { THEME } from 'frontend-utils/utils/prism/theme';
import GeneralDropdownsDropdownArrowButton from 'lumen/components/general/dropdowns/dropdown-arrow-button';
import GeneralTooltipsTooltipPropertyDetails from 'lumen/components/general/tooltips/tooltip-property-details';
import importAsset from 'lumen/helpers/import-asset';
import type { ShortTermRentalPropertyDetail } from 'lumen/models';
import getElement from 'lumen/modifiers/get-element';
import initGoogleMap from 'lumen/modifiers/init-google-map';
import { GOOGLE_MAPS_OWN_HOTEL } from 'lumen/utils/constants/google-maps-constants';
import {
  CLUSTER_PROPS,
  MAP_CLUSTER_SIZE,
  MARKER_PROPS,
} from 'lumen/utils/constants/short-term-rental-constants';

import styles from './short-term-rental-map.module.css';

interface HotelShortTermRentalShortTermRentalMapSignature {
  Args: {
    events: unknown;
    isLoading: unknown;
    mapData: unknown;
    radius: unknown;
    singleDaySelected: unknown;
  };
}

export default class HotelShortTermRentalShortTermRentalMap extends Component<HotelShortTermRentalShortTermRentalMapSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare store: Services['store'];
  @service declare theme: Services['theme'];

  @tracked currentlyHoveredEvent = {};
  @tracked hoverTooltipData = {};
  @tracked isMapLegendInitialLoad = true;
  @tracked isMapLegendVisible = true;
  // @ts-expect-error: Incorrect type
  @tracked markerEventListener;
  @tracked tooltipData = null;

  circle = null;
  clusterer = null;
  eventMarkerInfoWindow = null;
  events = [];
  eventTooltipElement = null;
  hoverTooltipElement = null;
  individualPropertyMarkerIcon = {
    path: 'M 5 5 L 5 24 L 24 24 L 24 5 L 5 5 z',
    fillColor: MARKER_PROPS.fillColor,
    fillOpacity: 0.6,
    strokeColor: MARKER_PROPS.strokeColor,
    strokeWeight: 2,
    rotation: 45,
    scale: 0.5,
    anchor: new google.maps.Point(0, 20),
  };
  initialized = false;
  ownHotelMarker = null;
  tooltip = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get legendFadeAnimationType() {
    if (!this.isMapLegendInitialLoad) {
      if (!this.isMapLegendVisible) {
        return 'fade-out';
      }
      return 'fade-in';
    }
    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelInfo() {
    return this.hotelAndMarketService.hotel?.hotelinfo;
  }

  willDestroy(): void {
    super.willDestroy();

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.idleMapListener?.remove();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.markerEventListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.circleEventListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.clickMapEventListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.dragMapEventListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.zoomMapEventListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.toggleAmenitiesListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.closeTooltipListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.mapEventListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.hoverTooltipMouseOverListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.hoverTooltipMouseOutListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.eventMouseOverListener?.remove();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.eventMouseOutListener?.remove();
    this.eventTooltipElement = null;
    this.hoverTooltipElement = null;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  clusterMarkerPath(size) {
    return `M-${size},0a${size},${size} 0 1,0 ${size * 2},0a${size},${size} 0 1,0 -${size * 2},0`;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  clusterSize(propertyListings) {
    const { small, medium } = CLUSTER_PROPS.thresholds;

    if (propertyListings > medium) {
      return MAP_CLUSTER_SIZE.LARGE;
    }
    if (propertyListings > small) {
      return MAP_CLUSTER_SIZE.MEDIUM;
    }

    return MAP_CLUSTER_SIZE.SMALL;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createInfoWindow() {
    return new google.maps.InfoWindow({
      minWidth: 300,
      maxWidth: 300,
      // @ts-expect-error: Incorrect type
      content: document.querySelector('#dummy-div-for-property-details-tooltip')
        .innerHTML,
    });
  }

  // @ts-expect-error: Incorrect type
  createLegend(map): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    map.controls[google.maps.ControlPosition.RIGHT_TOP].push(
      document.querySelector(`.${styles['map-legend-group']}`),
    );
  }

  // @ts-expect-error: Incorrect type
  drawCircle(map, ownHotelCoordinates): void {
    // @ts-expect-error: Incorrect type
    this.circle = new google.maps.Circle({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      map,
      fillColor: getCssVariableValue(COLOR_SHORT_TERM_RENTAL_RANGE),
      fillOpacity: 0.15,
      strokeWeight: 0,
      clickable: false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      center: ownHotelCoordinates,
      // @ts-expect-error: Incorrect type
      radius: this.args.radius * 1000, // Km to meter
    });
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  drawCompetitors(map) {
    const icon = {
      path: google.maps.SymbolPath.CIRCLE,
      fillOpacity: 1,
      scale: 8,
      strokeWeight: 2,
      strokeColor: getCssVariableValue('--prism-color-border-inverse-emphasis'),
      fillColor: getCssVariableValue(
        '--prism-color-chart-accent-primary-figure-emphasis',
      ),
      // Override the default anchor point to make the marker appear at the correct location
      anchor: new google.maps.Point(0, 1),
    };

    return (
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.primaryCompset.competitors.map(
        (competitor) => {
          const marker = new google.maps.Marker({
            position: {
              lat: competitor.latitude,
              lng: competitor.longitude,
            },
            icon,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            map,
          });
          marker.setCursor('default');

          // @ts-expect-error: Incorrect type
          if (!this.hoverTooltip) {
            // @ts-expect-error: Incorrect type
            this.hoverTooltip = new google.maps.InfoWindow({
              content: this.hoverTooltipElement,
            });
          }

          // @ts-expect-error: Incorrect type
          this.hoverTooltipMouseOverListener = marker.addListener(
            'mouseover',
            () => {
              this.hoverTooltipData = {
                name: competitor.name,
              };

              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              this.hoverTooltip.open({
                anchor: marker,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                map,
              });
            },
          );
          // @ts-expect-error: Incorrect type
          this.hoverTooltipMouseOutListener = marker.addListener(
            'mouseout',
            () => {
              // @ts-expect-error: Incorrect type
              this.hoverTooltipData = null;
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              this.hoverTooltip.close();
            },
          );
          return marker;
        },
      ) ?? []
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  drawEvents(map) {
    const isLightTheme =
      this.theme.currentTheme === THEME.LIGHT ||
      this.theme.currentTheme === THEME.HIGH_CONTRAST_LIGHT;

    const starIcon = {
      url: isLightTheme
        ? importAsset('images/star-icon.svg')
        : importAsset('images/star-icon-dark.svg'),
      anchor: new google.maps.Point(8, 8),
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.events =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.events
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .filter((event) => event.isAccepted)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .map((event) => {
          const marker = new google.maps.Marker({
            position: {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              lat: event.lat,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              lng: event.lon,
            },
            icon: starIcon,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            map,
          });
          marker.setCursor('default');

          if (!this.eventMarkerInfoWindow) {
            // @ts-expect-error: Incorrect type
            this.eventMarkerInfoWindow = new google.maps.InfoWindow({
              content: this.eventTooltipElement,
            });
          }

          // @ts-expect-error: Incorrect type
          this.eventMouseOverListener = marker.addListener('mouseover', () => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.currentlyHoveredEvent = event;

            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.eventMarkerInfoWindow.open({
              anchor: marker,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              map,
            });
          });
          // @ts-expect-error: Incorrect type
          this.eventMouseOutListener = marker.addListener('mouseout', () => {
            // @ts-expect-error: Incorrect type
            this.currentlyHoveredEvent = null;
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.eventMarkerInfoWindow.close();
          });
          return marker;
        }) ?? [];
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchMarkers(map) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.mapData?.map((location) => {
        const marker = new google.maps.Marker({
          icon: this.individualPropertyMarkerIcon,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          position: location,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          title: location.name,
          visible: true,
        });

        this.markerEventListener = marker.addListener('click', async () => {
          this.closeTooltip();

          try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const property = await this.requestIndividualProperty(location.id);
            begin();
            // @ts-expect-error: Incorrect type
            this.tooltipData = property;
            end();
            // @ts-expect-error: Incorrect type
            this.tooltip = this.createInfoWindow();

            // @ts-expect-error: Incorrect type
            this.mapEventListener = google.maps.event.addListenerOnce(
              // @ts-expect-error: Incorrect type
              this.tooltip,
              'domready',
              () => {
                const btnClose = document.getElementById('close-tooltip');
                const toggleAmenities = document.querySelector(
                  '.toggle-amenities-box',
                );
                const amenitiesBox = document.querySelector(
                  '.amenities-box-container',
                );
                const iconToggleAmenities = document.getElementById(
                  'icon-toggle-amenities',
                );

                // @ts-expect-error: Incorrect type
                this.toggleAmenitiesListener = toggleAmenities.addEventListener(
                  'click',
                  () => {
                    // @ts-expect-error: Incorrect type
                    iconToggleAmenities.classList.toggle('expanded');
                    // @ts-expect-error: Incorrect type
                    amenitiesBox.classList.toggle('expanded');
                    // redraw the tooltip when the amenities are expanded
                    // @ts-expect-error: Incorrect type
                    if (amenitiesBox.classList.contains('expanded')) {
                      // @ts-expect-error: Incorrect type
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                      this.tooltip.open({ anchor: marker });
                    }
                  },
                );

                // @ts-expect-error: Incorrect type
                this.closeTooltipListener = btnClose.addEventListener(
                  'click',
                  () => {
                    // @ts-expect-error: Incorrect type
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    this.tooltip.close();
                    // @ts-expect-error: Incorrect type
                    toggleAmenities.remove();
                    // @ts-expect-error: Incorrect type
                    amenitiesBox.remove();
                    // @ts-expect-error: Incorrect type
                    iconToggleAmenities.remove();
                    // @ts-expect-error: Incorrect type
                    btnClose.remove();
                  },
                );
              },
            );

            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.tooltip.open({
              anchor: marker,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              map,
            });
          } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.alerting.showErrorAlert();
            this.errorTracking.captureException(e);
          }
        });

        return marker;
      }) ?? []
    );
  }

  // Receives a LatLngBounds object and returns a slightly zoomed out version of it
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getExtendedBounds(bounds, zoom) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const extendedBounds = bounds;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const pointNorthEast = extendedBounds.getNorthEast();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const pointSouthWest = extendedBounds.getSouthWest();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const lngDifference = (pointNorthEast.lng() - pointSouthWest.lng()) * zoom;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const latDifference = (pointNorthEast.lat() - pointSouthWest.lat()) * zoom;

    const extendedPointNorthEast = new google.maps.LatLng(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      pointNorthEast.lat() + latDifference,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      pointNorthEast.lng() + lngDifference,
    );
    const extendedPointSouthWest = new google.maps.LatLng(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      pointSouthWest.lat() - latDifference,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      pointSouthWest.lng() - lngDifference,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    extendedBounds.extend(extendedPointNorthEast);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    extendedBounds.extend(extendedPointSouthWest);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return extendedBounds;
  }

  // Clear the radius,markers,cluster,etc. of the map
  purgeMap(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.circle?.setMap(null);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.clusterer?.clearMarkers();
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.ownHotelMarker?.setMap(null);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    this.events.map((event) => event.setMap(null));
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async requestIndividualProperty(propertyId) {
    const params = {
      typeKey: 'shorttermrental/properties',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      property_id: propertyId,
      // @ts-expect-error: Incorrect type
      subscription_id: this.hotelAndMarketService.hotel.id,
    };

    const properties = await this.store.query<ShortTermRentalPropertyDetail>(
      'short-term-rental-property-detail',
      params,
    );
    // Result will always contain only one property
    return properties[0];
  }

  @action
  closeTooltip(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const isTooltipOpen = this.tooltip?.getMap();
    if (isTooltipOpen) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.tooltip.close();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  setEventTooltipElement(element): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.eventTooltipElement = element;
  }

  @action
  // @ts-expect-error: Incorrect type
  setHoverTooltipElement(element): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.hoverTooltipElement = element;
  }

  @action
  toggleMapLegend(): void {
    this.isMapLegendVisible = !this.isMapLegendVisible;

    if (this.isMapLegendInitialLoad) {
      // Enables the animations from now on
      this.isMapLegendInitialLoad = false;
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async updateMapData(map) {
    const ownHotelCoordinates = {
      // @ts-expect-error: Incorrect type
      lat: this.ownHotelInfo.latitude,
      // @ts-expect-error: Incorrect type
      lng: this.ownHotelInfo.longitude,
    };

    this.purgeMap();
    this.createLegend(map);
    this.drawCircle(map, ownHotelCoordinates);
    if (!this.initialized) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      map.fitBounds(this.circle.getBounds());
    }

    const ownHotelMarkerIcon = {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: GOOGLE_MAPS_OWN_HOTEL,
      fillOpacity: 0.8,
      strokeColor: 'transparent',
      scale: 7,
    };

    // @ts-expect-error: Incorrect type
    this.ownHotelMarker = new google.maps.Marker({
      position: ownHotelCoordinates,
      icon: ownHotelMarkerIcon,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      map,
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + 1,
    });
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.ownHotelMarker.setCursor('default');

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    this.hoverTooltipMouseOverListener = this.ownHotelMarker.addListener(
      'mouseover',
      () => {
        this.hoverTooltipData = {
          // @ts-expect-error: Incorrect type
          name: this.ownHotelInfo.name,
        };

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.hoverTooltip.open({
          anchor: this.ownHotelMarker,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          map,
        });
      },
    );
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    this.hoverTooltipMouseOutListener = this.ownHotelMarker.addListener(
      'mouseout',
      () => {
        // @ts-expect-error: Incorrect type
        this.hoverTooltipData = null;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.hoverTooltip.close();
      },
    );

    if (this.args.singleDaySelected) {
      this.drawEvents(map);
    }
    this.drawCompetitors(map);

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/unbound-method
    this.clickMapEventListener = map.addListener('click', this.closeTooltip);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/unbound-method
    this.dragMapEventListener = map.addListener('drag', this.closeTooltip);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.zoomMapEventListener = map.addListener(
      'zoom_changed',
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.closeTooltip,
    );

    // @ts-expect-error: Incorrect type
    this.circleEventListener = google.maps.event.addListener(
      // @ts-expect-error: Incorrect type
      this.circle,
      'click',
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.closeTooltip,
    );

    // Customize the clusters
    const renderer = {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      render: ({ count, position }) => {
        const clusterSize = this.clusterSize(count);

        const clusterMarker = {
          // @ts-expect-error: Incorrect type
          path: this.clusterMarkerPath(CLUSTER_PROPS.widths[clusterSize]),
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          fillColor: CLUSTER_PROPS.colors.bg[clusterSize],
          fillOpacity: 0.8,
          strokeColor: 'transparent',
          strokeWeight: 0,
          scale: 0.5,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          anchor: new google.maps.Point(0, CLUSTER_PROPS.widths[clusterSize]),
        };

        return new google.maps.Marker({
          icon: clusterMarker,
          label: {
            text: String(count),
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            color: CLUSTER_PROPS.colors.text[clusterSize],
            fontSize: '12px',
            fontWeight: '700',
          },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          position,
          zIndex: google.maps.Marker.MAX_ZINDEX,
        });
      },
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const markers = await this.fetchMarkers(map);
    // @ts-expect-error: Incorrect type
    this.clusterer = new MarkerClusterer({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      markers,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      map,
      algorithm: new SuperClusterAlgorithm({ radius: 150, minPoints: 4 }),
      renderer,
    });

    // Lazy load markers for performance improvements
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.idleMapListener = google.maps.event.addListener(map, 'idle', () => {
      // Load markers for 30% around the visible canvas so the clusters don't change drastically moving across the map.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const extendedBounds = this.getExtendedBounds(map.getBounds(), 0.3);
      // Reload the markers on the map after panning/zooming
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.clusterer.clearMarkers();
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.clusterer.addMarkers(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        markers.filter((marker) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          extendedBounds.contains(marker.getPosition()),
        ),
      );
    });

    if (!this.initialized) {
      this.initialized = true;
    }
  }

  <template>
    <div
      class="google-maps-container rounded-200 shadow-neutral-100"
      id="ic-product-tour-rental-map"
      {{! @glint-expect-error: Incorrect type }}
      {{initGoogleMap this.updateMapData @isLoading}}
    ></div>
    <div class="hidden" id="dummy-div-for-property-details-tooltip">
      <div class={{styles.map-legend-group}}>
        <button
          button-type="unstyled"
          class={{concat
            styles.legend-trigger
            " "
            "button--unstyled flex items-center justify-center p-200 pr-300 pl-300"
          }}
          type="button"
          {{on "click" this.toggleMapLegend}}
        >
          {{t "market_insight.map.legend.trigger"}}
          <GeneralDropdownsDropdownArrowButton
            @isOpen={{this.isMapLegendVisible}}
          />
        </button>
        <div
          class="{{concat
              styles.map-overlay
              ' '
              styles.map-overlay--legend
              ' '
              'text-200--regular p-300'
            }}
            {{this.legendFadeAnimationType}}
            "
        >
          <div class="flex items-center justify-start">
            <div
              class={{concat
                styles.compset-dot-indicator
                " "
                styles.compset-dot-indicator--own-hotel
                " "
                "mr-200"
              }}
            ></div>
            {{t "general.my_hotel"}}
          </div>
          <div class="flex items-center justify-start">
            <div
              class={{concat
                styles.compset-dot-indicator
                " "
                "compset-dot-indicator--short-term-rental mr-200"
              }}
            ></div>
            {{t "general.short_term_rental"}}
          </div>
          <div class="flex items-center justify-start">
            <div
              class={{concat
                styles.compset-dot-indicator
                " "
                styles.compset-dot-indicator--own-compset
                " "
                "mr-200"
              }}
            ></div>
            {{t "market_insight.map.legend.own_compset"}}
          </div>
          <div
            class={{concat
              styles.map-overlay--legend__item
              " "
              "flex items-center justify-start"
            }}
          >
            <div class="events">
              <PrismIcon @color="inherit" @name="star-filled" @size="small" />
            </div>
            {{t "events.accepted_events"}}
          </div>
        </div>
      </div>

      {{#if this.tooltipData}}
        <GeneralTooltipsTooltipPropertyDetails @property={{this.tooltipData}} />
      {{/if}}
    </div>
    <div class="hidden">
      <div class={{styles.tooltip}} {{getElement this.setEventTooltipElement}}>
        {{! @glint-expect-error: Incorrect type }}
        <p class="title">{{this.currentlyHoveredEvent.name}}</p>
        <div class={{styles.tooltip__divider}}></div>
        {{! @glint-expect-error: Incorrect type }}
        {{#if this.currentlyHoveredEvent.displayCategory}}
          <div class={{styles.tooltip__metric}}>
            <p>
              <span class="mr-100">
                {{! @glint-expect-error: Incorrect type }}
                <PrismIcon @name={{this.currentlyHoveredEvent.icon}} />
              </span>
              {{! @glint-expect-error: Incorrect type }}
              {{this.currentlyHoveredEvent.displayCategory}}
            </p>
          </div>
        {{/if}}
        {{! @glint-expect-error: Incorrect type }}
        {{#if this.currentlyHoveredEvent.visitorsFormatted}}
          <div class={{styles.tooltip__metric}}>
            <p>
              <span class="mr-100">
                <PrismIcon @name="user-group" />
              </span>
              {{! @glint-expect-error: Incorrect type }}
              {{this.currentlyHoveredEvent.visitorsFormatted}}
            </p>
          </div>
        {{/if}}
        {{! @glint-expect-error: Incorrect type }}
        {{#if this.currentlyHoveredEvent.formattedDays}}
          <div class={{styles.tooltip__metric}}>
            <p>
              <span class="mr-100">
                <PrismIcon @name="calendar-default" />
              </span>
              {{! @glint-expect-error: Incorrect type }}
              {{this.currentlyHoveredEvent.formattedDays}}
            </p>
          </div>
        {{/if}}
      </div>
      <div {{getElement this.setHoverTooltipElement}}>
        {{! @glint-expect-error: Incorrect type }}
        <span>{{this.hoverTooltipData.name}}</span>
      </div>
    </div>
  </template>
}
